import React, { useMemo, useState } from "react";
import { App, Form, Input, InputNumber, Modal, Select, Typography } from "antd";
import { useAuth } from "../../../context/AuthContext";
import { createBudgetExtensionRequest, listRbacProjects } from "../../../services/api";

const { Text } = Typography;

export default function RequestExtensionModal({
  open,
  onClose,
  defaultProjectId = null,
  onCreated,
  eligibleProjectIds = null,
}) {
  const { token } = useAuth();
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);

  const initial = useMemo(
    () => ({
      project_id: defaultProjectId || null,
      requested_amount_usd: null,
      reason: "",
    }),
    [defaultProjectId],
  );

  const loadProjects = async () => {
    if (!token) return;
    setLoadingProjects(true);
    try {
      const res = await listRbacProjects(token);
      setProjects(Array.isArray(res?.data) ? res.data : []);
    } catch {
      setProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  };

  const handleOk = async () => {
    const v = await form.validateFields();
    setSubmitting(true);
    try {
      const res = await createBudgetExtensionRequest(token, {
        project_id: v.project_id,
        requested_amount_usd: Number(v.requested_amount_usd),
        reason: v.reason,
      });
      message.success("Request submitted");
      if (typeof onCreated === "function") onCreated(res?.data?.request || null);
      onClose();
    } catch (e) {
      message.error(e?.response?.data?.detail || "Failed to submit request");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title="Request budget extension"
      open={open}
      onCancel={onClose}
      onOk={handleOk}
      okText="Submit request"
      confirmLoading={submitting}
      afterOpenChange={(isOpen) => {
        if (isOpen) {
          form.setFieldsValue(initial);
          loadProjects();
        }
      }}
    >
      <Form form={form} layout="vertical" initialValues={initial}>
        <Form.Item
          label="Project"
          name="project_id"
          rules={[{ required: true, message: "Please choose a project" }]}
        >
          <Select
            showSearch
            loading={loadingProjects}
            placeholder="Select project"
            optionFilterProp="label"
            options={(projects || [])
              .filter((p) => {
                if (!p?.project_id) return false;
                if (eligibleProjectIds == null) return true;
                return eligibleProjectIds.includes(p.project_id);
              })
              .map((p) => ({ value: p.project_id, label: p.name || p.project_id }))}
          />
        </Form.Item>

        <Form.Item
          label="Requested additional budget (USD)"
          name="requested_amount_usd"
          rules={[{ required: true, message: "Enter an amount" }]}
        >
          <InputNumber min={0.01} precision={2} style={{ width: "100%" }} placeholder="e.g. 25.00" />
        </Form.Item>

        <Form.Item
          label="Reason"
          name="reason"
          rules={[{ required: true, message: "Please provide a reason" }]}
        >
          <Input.TextArea rows={4} placeholder="Why do you need the extension?" />
        </Form.Item>

        <Text type="secondary">
          Available when you have used at least 80% of your allocated budget for the selected project.
          Track status in My Usage.
        </Text>
      </Form>
    </Modal>
  );
}

